import {
    memo,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { createPortal } from "react-dom";

import { ForceCaptureConfirmMode } from "@/components/GalleryUploader/Camera/modes/ForceCaptureConfirmMode";
import { UseDeviceOrientationReturn } from "@/hooks/useDeviceOrientation";
import { useStableRef } from "@/hooks/useStableRef";

import { CameraMode, ViewfinderProps } from "./Camera.types";
import { CapturedMode, EditingMode, PreviewMode } from "./modes";
import { StyledCamera } from "./Styled";

export interface CameraProps {
    open: boolean;
    onClose: () => void;
    onCapture: (blob: Blob) => Promise<void> | void;

    orientation?: UseDeviceOrientationReturn;
    viewfinder?: React.ComponentType<ViewfinderProps>;
    previewOverlay?: ReactNode;
}

export const Camera = memo(
    ({
        open,
        onClose,
        onCapture,
        viewfinder: Viewfinder,
        orientation,
        previewOverlay,
    }: CameraProps) => {
        const onCloseRef = useStableRef(onClose);
        const onCaptureRef = useStableRef(onCapture);

        const [mode, setMode] = useState<CameraMode>(CameraMode.preview);
        const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);

        const capturedImageUrl = useMemo(() => {
            return capturedBlob ? URL.createObjectURL(capturedBlob) : null;
        }, [capturedBlob]);

        useEffect(() => {
            if (!open) {
                setCapturedBlob(null);
                setMode(CameraMode.preview);
            } else {
                setCapturedBlob(null);
                setMode(CameraMode.preview);
            }
        }, [open]);

        useEffect(() => {
            return () => {
                if (capturedImageUrl) {
                    URL.revokeObjectURL(capturedImageUrl);
                }
            };
        }, [capturedImageUrl]);

        const handleCancel = useCallback(() => {
            setCapturedBlob(null);
            setMode(CameraMode.preview);
            onCloseRef.current();
        }, [onCloseRef]);

        const handleCapture = useCallback(
            ({ blob, mode }: { blob: Blob; mode: CameraMode }) => {
                setCapturedBlob(blob);
                setMode(mode);
            },
            [],
        );

        const handleRetake = useCallback(() => {
            setCapturedBlob(null);
            setMode(CameraMode.preview);
        }, []);

        const handleStartEdit = useCallback(() => {
            setMode(CameraMode.editing);
        }, []);

        const handleSaveEdit = useCallback((editedBlob: Blob) => {
            setCapturedBlob(editedBlob);
            setMode(CameraMode.captured);
        }, []);

        const handleCancelEdit = useCallback(() => {
            setMode(CameraMode.captured);
        }, []);

        const handleConfirm = useCallback(async () => {
            if (!capturedBlob) return;

            await onCaptureRef.current(capturedBlob);

            setCapturedBlob(null);
            setMode(CameraMode.preview);
            onCloseRef.current();
        }, [capturedBlob, onCaptureRef, onCloseRef]);

        const handleUserMediaError = useCallback(() => {
            onCloseRef.current();
        }, [onCloseRef]);

        const oriEnabled = !!orientation?.enabled && !!orientation?.supported;
        const levelOk = !!orientation?.enabled && !!orientation?.isValid;

        if (!open) return null;

        return createPortal(
            <StyledCamera>
                {mode === CameraMode.preview && (
                    <PreviewMode
                        Viewfinder={Viewfinder}
                        levelOk={levelOk}
                        oriEnabled={oriEnabled}
                        onCancel={handleCancel}
                        onCapture={handleCapture}
                        onUserMediaError={handleUserMediaError}
                        previewOverlay={previewOverlay}
                    />
                )}

                {mode === CameraMode.captured && capturedImageUrl && (
                    <CapturedMode
                        capturedImage={capturedImageUrl}
                        onRetake={handleRetake}
                        onEdit={handleStartEdit}
                        onConfirm={handleConfirm}
                    />
                )}

                {mode === CameraMode.forceCaptureConfirm &&
                    capturedImageUrl && (
                        <ForceCaptureConfirmMode
                            capturedImage={capturedImageUrl}
                            onRetake={handleRetake}
                            onConfirm={() => setMode(CameraMode.captured)}
                        />
                    )}

                {mode === CameraMode.editing && capturedImageUrl && (
                    <EditingMode
                        capturedImage={capturedImageUrl}
                        onCancel={handleCancelEdit}
                        onSave={handleSaveEdit}
                    />
                )}
            </StyledCamera>,
            document.body,
        );
    },
);

Camera.displayName = "Camera";
